import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Receipt, Plus, Search, Download, Send, Eye, CheckCircle2, 
  Clock, XCircle, Building2, User, FileText, IndianRupee,
  Calendar, Printer
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const InvoicesPage = () => {
  const { user, hasRole, builder } = useAuth();
  const isBuilder = hasRole(['builder_admin', 'super_admin']);
  const isPartner = hasRole(['partner', 'sub_user']);
  
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projects, setProjects] = useState([]);
  const [commissions, setCommissions] = useState([]);
  
  // Form state for creating invoice
  const [formData, setFormData] = useState({
    commission_id: '',
    company_name: '',
    gst_number: '',
    pan_number: '',
    address: '',
    bank_name: '',
    bank_account: '',
    bank_ifsc: '',
    notes: '',
  });

  useEffect(() => {
    loadInvoices();
    loadProjects();
    if (isPartner) {
      loadCommissions();
    }
  }, []);

  const loadInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data);
      }
    } catch (error) {
      console.error('Failed to load invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const loadCommissions = async () => {
    try {
      const token = localStorage.getItem('token');
      // First get pipeline stages to find which stages are "win" stages
      const stagesRes = await fetch(`${API}/api/crm/pipeline-stages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let winStageIds = [];
      if (stagesRes.ok) {
        const stages = await stagesRes.json();
        winStageIds = stages.filter(s => s.is_win_stage).map(s => s.id);
      }
      
      // Get commissions - these should be from "closed won" deals
      const response = await fetch(`${API}/api/commissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter commissions:
        // 1. Not already invoiced
        // 2. Lead is in a win stage OR commission status is "approved"
        const eligibleCommissions = data.filter(c => {
          const isNotInvoiced = !c.invoice_generated;
          const isWinStage = c.customer?.status && winStageIds.includes(c.customer.status);
          const isApproved = c.status === 'approved';
          return isNotInvoiced && (isWinStage || isApproved);
        });
        setCommissions(eligibleCommissions);
      }
    } catch (error) {
      console.error('Failed to load commissions:', error);
    }
  };

  const handleCreateInvoice = async () => {
    if (!formData.commission_id) {
      toast.error('Please select a commission to invoice');
      return;
    }
    if (!formData.company_name || !formData.gst_number || !formData.pan_number) {
      toast.error('Please fill in company details, GST and PAN');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/invoices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Invoice created and sent to builder!');
        setDialogOpen(false);
        setFormData({
          commission_id: '',
          company_name: '',
          gst_number: '',
          pan_number: '',
          address: '',
          bank_name: '',
          bank_account: '',
          bank_ifsc: '',
          notes: '',
        });
        loadInvoices();
        loadCommissions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create invoice');
      }
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  const handleUpdateStatus = async (invoiceId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/invoices/${invoiceId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success(`Invoice ${newStatus}`);
        loadInvoices();
        setViewDialogOpen(false);
      }
    } catch (error) {
      toast.error('Failed to update invoice status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-green-100 text-green-700',
      paid: 'bg-blue-100 text-blue-700',
      rejected: 'bg-red-100 text-red-700',
    };
    return <Badge className={styles[status] || styles.draft}>{status}</Badge>;
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${amount.toLocaleString()}`;
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch = 
      invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.buyer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const selectedCommission = commissions.find(c => c.id === formData.commission_id);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Receipt className="h-7 w-7 text-amber-600" />
            {isPartner ? 'My Invoices' : 'Commission Invoices'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isPartner ? 'Create and manage invoices for your commissions' : 'Review and process partner invoices'}
          </p>
        </div>
        {isPartner && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-create-invoice">
                <Plus className="h-4 w-4 mr-2" />
                Create Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Commission Invoice</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Select Commission */}
                <div className="space-y-2">
                  <Label>Select Commission *</Label>
                  <Select 
                    value={formData.commission_id} 
                    onValueChange={(value) => setFormData({...formData, commission_id: value})}
                  >
                    <SelectTrigger data-testid="select-commission">
                      <SelectValue placeholder="Select pending commission" />
                    </SelectTrigger>
                    <SelectContent>
                      {commissions.length === 0 ? (
                        <div className="p-4 text-center text-muted-foreground">
                          No pending commissions available for invoicing
                        </div>
                      ) : (
                        commissions.map((comm) => (
                          <SelectItem key={comm.id} value={comm.id}>
                            {comm.customer?.name || 'Customer'} - {comm.inventory?.unit_number || comm.unit_number || 'Unit'} ({formatCurrency(comm.commission_amount || comm.amount)})
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {selectedCommission && (
                  <Card className="bg-muted/30">
                    <CardContent className="p-4">
                      <h4 className="font-medium mb-2">Commission Details</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-muted-foreground">Buyer:</span> {selectedCommission.customer?.name || 'N/A'}</div>
                        <div><span className="text-muted-foreground">Unit:</span> {selectedCommission.inventory?.unit_number || 'N/A'}</div>
                        <div><span className="text-muted-foreground">Booking Value:</span> {formatCurrency(selectedCommission.booking_value)}</div>
                        <div><span className="text-muted-foreground">Commission ({selectedCommission.commission_percentage}%):</span> {formatCurrency(selectedCommission.commission_amount)}</div>
                        <div><span className="text-muted-foreground">Project:</span> {selectedCommission.project?.name || 'N/A'}</div>
                        <div><span className="text-muted-foreground">Tier:</span> {selectedCommission.partner?.tier || 'N/A'}</div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Your Company Details</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Company Name *</Label>
                      <Input
                        value={formData.company_name}
                        onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                        placeholder="Your company name"
                        data-testid="input-company-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>GST Number *</Label>
                      <Input
                        value={formData.gst_number}
                        onChange={(e) => setFormData({...formData, gst_number: e.target.value.toUpperCase()})}
                        placeholder="e.g., 22AAAAA0000A1Z5"
                        data-testid="input-gst"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>PAN Number *</Label>
                      <Input
                        value={formData.pan_number}
                        onChange={(e) => setFormData({...formData, pan_number: e.target.value.toUpperCase()})}
                        placeholder="e.g., ABCDE1234F"
                        data-testid="input-pan"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Address</Label>
                      <Input
                        value={formData.address}
                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                        placeholder="Company address"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Bank Details (for payment)</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Bank Name</Label>
                      <Input
                        value={formData.bank_name}
                        onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                        placeholder="Bank name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Account Number</Label>
                      <Input
                        value={formData.bank_account}
                        onChange={(e) => setFormData({...formData, bank_account: e.target.value})}
                        placeholder="Account number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>IFSC Code</Label>
                      <Input
                        value={formData.bank_ifsc}
                        onChange={(e) => setFormData({...formData, bank_ifsc: e.target.value.toUpperCase()})}
                        placeholder="IFSC code"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes / Remarks</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Any additional notes..."
                    rows={2}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateInvoice} data-testid="btn-submit-invoice">
                  <Send className="h-4 w-4 mr-2" />
                  Create & Send Invoice
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{invoices.length}</p>
                <p className="text-sm text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{invoices.filter(i => i.status === 'pending').length}</p>
                <p className="text-sm text-muted-foreground">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{invoices.filter(i => i.status === 'approved').length}</p>
                <p className="text-sm text-muted-foreground">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-100 rounded-lg">
                <IndianRupee className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{invoices.filter(i => i.status === 'paid').length}</p>
                <p className="text-sm text-muted-foreground">Paid</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by invoice number, company, or buyer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="input-search-invoices"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-48" data-testid="filter-status">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Invoice List */}
      {filteredInvoices.length === 0 ? (
        <Card className="p-8 text-center">
          <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No Invoices Found</h3>
          <p className="text-muted-foreground mb-4">
            {isPartner 
              ? "You haven't created any invoices yet. Create one for your approved commissions."
              : "No invoices have been submitted yet."}
          </p>
          {isPartner && commissions.length > 0 && (
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Invoice
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredInvoices.map((invoice) => (
            <Card key={invoice.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-muted rounded-lg">
                      <Receipt className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{invoice.invoice_number}</h3>
                        {getStatusBadge(invoice.status)}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium">{invoice.company_name}</span>
                        {invoice.gst_number && ` • GST: ${invoice.gst_number}`}
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm">
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {invoice.buyer_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          {invoice.unit_number}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(invoice.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(invoice.amount)}</p>
                      <p className="text-xs text-muted-foreground">Commission Amount</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setSelectedInvoice(invoice);
                        setViewDialogOpen(true);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Invoice Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Invoice Details</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              {/* Invoice Header */}
              <div className="flex justify-between items-start p-4 bg-muted/30 rounded-lg">
                <div>
                  <h3 className="text-xl font-bold">{selectedInvoice.invoice_number}</h3>
                  <p className="text-sm text-muted-foreground">
                    Created: {new Date(selectedInvoice.created_at).toLocaleDateString()}
                  </p>
                </div>
                {getStatusBadge(selectedInvoice.status)}
              </div>

              {/* From / To */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 border rounded-lg">
                  <p className="text-xs uppercase text-muted-foreground mb-2">From (Partner)</p>
                  <p className="font-semibold">{selectedInvoice.company_name}</p>
                  <p className="text-sm">GST: {selectedInvoice.gst_number}</p>
                  <p className="text-sm">PAN: {selectedInvoice.pan_number}</p>
                  {selectedInvoice.address && <p className="text-sm text-muted-foreground mt-1">{selectedInvoice.address}</p>}
                </div>
                <div className="p-4 border rounded-lg">
                  <p className="text-xs uppercase text-muted-foreground mb-2">To (Builder)</p>
                  <p className="font-semibold">{builder?.name || 'Builder'}</p>
                </div>
              </div>

              {/* Commission Details */}
              <div className="p-4 border rounded-lg">
                <p className="text-xs uppercase text-muted-foreground mb-2">Commission Details</p>
                <div className="grid grid-cols-2 gap-y-2 text-sm">
                  <div><span className="text-muted-foreground">Buyer:</span></div>
                  <div className="font-medium">{selectedInvoice.buyer_name}</div>
                  <div><span className="text-muted-foreground">Unit:</span></div>
                  <div className="font-medium">{selectedInvoice.unit_number}</div>
                  <div><span className="text-muted-foreground">Project:</span></div>
                  <div className="font-medium">{selectedInvoice.project_name}</div>
                </div>
              </div>

              {/* Amount */}
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Total Amount</span>
                  <span className="text-2xl font-bold text-green-600">{formatCurrency(selectedInvoice.amount)}</span>
                </div>
              </div>

              {/* Bank Details */}
              {selectedInvoice.bank_name && (
                <div className="p-4 border rounded-lg">
                  <p className="text-xs uppercase text-muted-foreground mb-2">Bank Details</p>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <p className="text-muted-foreground">Bank</p>
                      <p className="font-medium">{selectedInvoice.bank_name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Account</p>
                      <p className="font-medium">{selectedInvoice.bank_account}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">IFSC</p>
                      <p className="font-medium">{selectedInvoice.bank_ifsc}</p>
                    </div>
                  </div>
                </div>
              )}

              {selectedInvoice.notes && (
                <div className="p-4 border rounded-lg">
                  <p className="text-xs uppercase text-muted-foreground mb-2">Notes</p>
                  <p className="text-sm">{selectedInvoice.notes}</p>
                </div>
              )}

              {/* Actions */}
              <DialogFooter className="gap-2">
                <Button variant="outline">
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
                <Button variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
                {isBuilder && selectedInvoice.status === 'pending' && (
                  <>
                    <Button 
                      variant="destructive"
                      onClick={() => handleUpdateStatus(selectedInvoice.id, 'rejected')}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                    <Button 
                      onClick={() => handleUpdateStatus(selectedInvoice.id, 'approved')}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                  </>
                )}
                {isBuilder && selectedInvoice.status === 'approved' && (
                  <Button 
                    onClick={() => handleUpdateStatus(selectedInvoice.id, 'paid')}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <IndianRupee className="h-4 w-4 mr-2" />
                    Mark as Paid
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesPage;
